/**
 * /organizations/new — submit an organization registration, as a page
 * (Feature 70, D5). It calls the public self-signup endpoint, so the
 * organization lands `pending` and appears on the Overview board for approval
 * like any other applicant. Platform admins use it to bootstrap a tenant
 * without the marketing site.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { registerOrganization } from '@optum/api-client/endpoints/registrations';

import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { formatRecordParam, RECORD_PARAM } from '../../components/shell/record.js';
import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Input } from '../../ui/finixt.js';
import { FormShape } from '../../ui/shapes.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useUnsavedChanges } from '../../lib/unsaved.js';

export function NewOrganizationPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  useBreadcrumbTail('New organization');
  const release = useUnsavedChanges(name !== '' || description !== '' || contactEmail !== '');

  const submit = useMutation({
    mutationFn: () =>
      registerOrganization({
        name,
        description: description || null,
        contact_email: contactEmail || null,
      }),
    onSuccess: (out) => {
      toast.success(`Submitted ${out.name}`, 'Approve it from the Overview board to activate.');
      qc.invalidateQueries({ queryKey: qk.registrations() });
      qc.invalidateQueries({ queryKey: qk.orgs() });
      release();
      const params = new URLSearchParams({
        [RECORD_PARAM]: formatRecordParam({ kind: 'organization', id: out.id }),
      });
      navigate(`/organizations?${params.toString()}`);
    },
    onError: (e) => toast.error('Submit failed', e instanceof ApiError ? e.message : String(e)),
  });

  function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (name) submit.mutate();
  }

  return (
    <FormShape
      title="New organization"
      subtitle="Lands as pending. Approve it from the Overview board to activate the organization."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field id="reg-name" label="Name" required>
          <Input
            id="reg-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            required
            autoFocus
          />
        </Field>
        <Field id="reg-desc" label="Description">
          <Input
            id="reg-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field id="reg-email" label="Contact email">
          <Input
            id="reg-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </Field>
        <div
          style={{
            display: 'flex',
            gap: 7,
            paddingTop: 16,
            borderTop: '1px solid var(--border-hairline)',
          }}
        >
          <Button type="submit" variant="primary" disabled={submit.isPending || !name}>
            {submit.isPending ? 'Submitting…' : 'Submit registration'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/organizations')}>
            Cancel
          </Button>
        </div>
      </form>
    </FormShape>
  );
}
